// src/components/cart/CartDrawer.jsx
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../CartContext";
import { productImageUrl, productImageAlt, productInStock } from "../../context/ProductDataContext";

const asNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const fmtFt = (value) => {
  const n = asNumber(value, NaN);
  return Number.isFinite(n) ? `${n.toLocaleString("hu-HU")} Ft` : "—";
};

const FREE_SHIP_THRESHOLD = 30000;

export default function CartDrawer() {
  const navigate = useNavigate();
  const { isOpen, closeCart, cartItems, inc, dec, setQuantity, removeFromCart, total } = useCart();

  const safeItems = Array.isArray(cartItems) ? cartItems : [];
  const count = useMemo(
    () => safeItems.reduce((s, i) => s + Math.max(1, asNumber(i?.quantity, 1)), 0),
    [safeItems]
  );
  const safeTotal = useMemo(
    () => safeItems.reduce((s, i) => s + asNumber(i?.price) * Math.max(1, asNumber(i?.quantity, 1)), 0),
    [safeItems]
  );
  const displayTotal = Number.isFinite(Number(total)) ? Number(total) : safeTotal;

  const remainingForFree = Math.max(0, FREE_SHIP_THRESHOLD - displayTotal);
  const progressPct = Math.min(100, Math.round((displayTotal / FREE_SHIP_THRESHOLD) * 100));

  const browseProducts = () => {
    closeCart?.();
    navigate("/kategoriak");
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-[60] transition ${isOpen ? "visible bg-black/40" : "invisible bg-transparent"}`}
        onClick={closeCart}
      />

      <aside
        className={`fixed right-0 top-0 h-full w-[92%] sm:w-[30rem] z-[61] bg-white shadow-2xl border-l
        transition-transform duration-300 ${isOpen ? "translate-x-0" : "translate-x-full"}
        flex flex-col`}
        role="dialog"
        aria-label="Kosár"
      >
        <header className="relative">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0c1a26] to-[#173047]" />
          <div className="relative px-4 pt-4 pb-3 text-white">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold">Kosár</h2>
              <span className="text-sm text-white/80">({count} tétel)</span>
              <button
                onClick={closeCart}
                className="ml-auto text-white/80 hover:text-white text-lg leading-none"
                aria-label="Kosár bezárása"
              >
                ✕
              </button>
            </div>
            <p className="text-xs text-white/80 mt-1">
              Ellenőrizd a tételeket, állítsd be a darabszámot, majd rendelj egy kattintással.
            </p>
          </div>
        </header>

        {safeItems.length > 0 && (
          <div className="px-4 pt-3 pb-2 border-b bg-white">
            <div className={`text-sm ${remainingForFree === 0 ? "text-emerald-700" : "text-gray-700"}`}>
              {remainingForFree === 0 ? (
                <span>🎉 Elérted az ingyenes szállítás küszöbét!</span>
              ) : (
                <span>
                  🚚 Még <strong>{fmtFt(remainingForFree)}</strong> az ingyenes szállításig
                </span>
              )}
            </div>
            <div className="mt-2 h-2 w-full rounded-full bg-gray-100 overflow-hidden">
              <div
                className={`h-full ${remainingForFree === 0 ? "bg-emerald-500" : "bg-blue-500"}`}
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        )}

        <div
          className="px-4 py-4 overflow-y-auto flex-1"
          style={{ paddingBottom: "calc(16px + env(safe-area-inset-bottom))" }}
        >
          {safeItems.length === 0 ? (
            <EmptyState onBrowse={browseProducts} />
          ) : (
            <ul className="space-y-3">
              {safeItems.map((it) => {
                const quantity = Math.max(1, asNumber(it?.quantity, 1));
                const price = asNumber(it?.price);
                const lineTotal = price * quantity;
                const id = it.id || it.slug;

                return (
                  <li key={id} className="rounded-2xl border bg-white p-3 shadow-sm">
                    <div className="flex gap-3">
                      <img
                        src={productImageUrl(it)}
                        alt={productImageAlt(it)}
                        className="w-20 h-20 rounded object-contain bg-gray-50 border"
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.src = "/img/placeholder.webp";
                        }}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium leading-snug line-clamp-2">{it.name}</div>
                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                          <span className="text-sm text-gray-600">{fmtFt(price)}</span>
                          {productInStock(it) ? (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                              Raktáron
                            </span>
                          ) : (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                              Rendelésre
                            </span>
                          )}
                        </div>

                        <div className="mt-3 flex items-center gap-2">
                          <button
                            className="w-8 h-8 border rounded hover:bg-gray-50"
                            onClick={() => dec(id)}
                            aria-label="Mennyiség csökkentése"
                          >
                            −
                          </button>
                          <input
                            className="w-14 h-8 border rounded text-center"
                            type="number"
                            min={1}
                            max={99}
                            value={quantity}
                            onChange={(e) => setQuantity(id, e.target.value)}
                            aria-label="Mennyiség"
                          />
                          <button
                            className="w-8 h-8 border rounded hover:bg-gray-50"
                            onClick={() => inc(id)}
                            aria-label="Mennyiség növelése"
                          >
                            +
                          </button>

                          <div className="ml-auto text-sm font-medium tabular-nums">{fmtFt(lineTotal)}</div>
                        </div>

                        <div className="mt-2">
                          <button
                            className="text-rose-600 text-xs hover:underline"
                            onClick={() => removeFromCart(id)}
                          >
                            Eltávolítás
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <footer
          className="sticky bottom-0 left-0 right-0 border-t bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/80"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <div className="px-4 py-3 space-y-3">
            <div className="flex items-center justify-between text-lg font-semibold">
              <span>Összesen</span>
              <span className="tabular-nums">{fmtFt(displayTotal)}</span>
            </div>

            <button
              className="w-full h-11 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
              disabled={safeItems.length === 0}
              onClick={() => {
                closeCart();
                navigate("/megrendeles");
              }}
            >
              Megrendelem
            </button>
          </div>
        </footer>
      </aside>
    </>
  );
}

function EmptyState({ onBrowse }) {
  return (
    <div className="rounded-2xl border bg-gradient-to-br from-blue-50 to-emerald-50 p-6 text-center">
      <div className="text-5xl">🛒</div>
      <h3 className="text-lg font-semibold mt-2">A kosarad üres</h3>
      <p className="text-sm text-gray-700 mt-1">
        Kezdd a böngészést — válogass okos izzókból, szenzorokból és központokból!
      </p>
      <button
        type="button"
        onClick={onBrowse}
        className="inline-flex items-center justify-center rounded-xl bg-[#0c1a26] text-white px-4 py-2 mt-4 hover:bg-black/90"
      >
        Termékek böngészése
      </button>
    </div>
  );
}
